import { fetchJson } from '@utils/fetchJson';
import { API_BASE_URL } from '@config';
export async function fetchCustomFieldsByMenuId(menuId, menuData = '{}', {
  defaultFormTemplate = {},
  validationRules = {},
  fieldOverrides = {},
  columnLabels = {},
  directSchemaTemplate = [],
  layout = [],
  autoInjectLayout = true, // control injection here
} = {}) {
  try {
    const res = await fetchJson(`${API_BASE_URL}/dynamicFormFieldList`, {
      method: 'POST',
      body: JSON.stringify({
        menuId: String(menuId),
        status: 'active',
        getAll: 'Y',
      }),
    });

    if (res.flag !== 'S') throw new Error(res.msg || 'Failed to load custom fields');

    const customFields = res.data || [];
    // const extendedDefaults = {
    //   ...defaultFormTemplate,
    //   ...Object.fromEntries(customFields.map((f) => [f.column_name, f.defaultValue || null])),
    //   menuId: menuId,
    // };

    const extendedDefaults = {
  ...Object.fromEntries(customFields.map((f) => [f.column_name, f.defaultValue || null])),
  ...defaultFormTemplate, // fallback only if not present
  menuId: menuId,
};

    const extendedValidations = {
      ...validationRules,
      ...Object.fromEntries(
        customFields
          .filter((f) => f.isRequired === 'yes')
          .map((f) => [f.column_name, { required: true }])
      ),
    };

    const extendedLabels = {
      ...columnLabels,
      ...Object.fromEntries(customFields.map((f) => [f.column_name, f.fieldLabel])),
    };
    const mapType = (type) => {
      console.log("type.fieldType");
    console.log(type.fieldType);
      switch (type.fieldType) {
        case 'Text': return 'text';
        case 'Textarea':
          return type.textareaType === 'richTextarea' ? 'editor' : 'textarea';
        case 'Datepicker': return 'date';
        case 'Numeric': return 'number';
        case 'Dropdown':
          return type.linked_with_name !== null ? 'smartSelectInput' : 'dropdown';
        case 'RadioList': return 'radio';
        case 'CheckboxList': return 'checkbox';
        default: return 'text';
      }
    };

    const getSmartSelectConfig = (field) => {
      const source = field.linked_with_name;
      return source === 'categories'
        ? {
            label: '',
            type: 'category',
            source: field.linkedDisplayColumn,
            placeholder: `Select ${field.fieldLabel}`,
            allowAddNew: true,
            preload: true,
            cache: true,
            showRecent: true,
            getLabel: (item) => `${item.categoryName}`,
            getValue: (item) => item.category_id,
          }
        : {
            label: '',
            type: source,
            source,
            valueKey: 'pkKey',
            check: field.linkedDisplayColumn,
            placeholder: `Select ${field.fieldLabel}`,
            allowAddNew: true,
            preload: true,
            cache: true,
            showRecent: true,
            getLabel: (item) => `${item[field.linkedDisplayColumn]}`,
            getValue: (item) => item.pkKey,
            multi: false,
            list: `${field.linkedDisplayColumn},pkKey`,
          };
    };
    const extendedOverrides = {
      ...fieldOverrides,
      ...Object.fromEntries(
        customFields.map((f) => {
          const type = mapType(f);
          const override = { type, label: f.fieldLabel };
          if (type === 'smartSelectInput') {
            override.config = getSmartSelectConfig(f);
          } else if (f.fieldOptions) {
            console.log(f.fieldOptions);
            override.options = f.fieldOptions;
            // override.options = f.fieldOptions.split(',').map((opt) => ({
            //   label: opt.trim(),
            //   value: opt.trim(),
            // }));
          }

          return [f.column_name, override];
        })
      ),
    };

    const extendedSchema = [
      ...directSchemaTemplate,
      ...customFields.map((f) => ({
        Field: f.column_name,
        Type: mapType(f.fieldType),
      })),
    ];

    const metadata = JSON.parse(menuData || '{}');
    const generatedLayout = [];
    Object.keys(metadata).forEach((rowKey, index) => {
      const row = metadata[rowKey];
      const layoutRow = { row: [] };
      if (index === 0) layoutRow.label = 'Custom Fields';

      const totalFieldsInRow =
        (row.col1?.length || 0) + (row.col2?.length || 0) + (row.col3?.length || 0);

      const getColClass = (count) => {
        if (count === 1 || count === 2) return 'w-full md:w-1/3 mb-3';
        return 'flex-1 mb-6 min-w-[100px]';
      };
      ['col1', 'col2', 'col3'].forEach((colKey) => {
        const colArr = Array.isArray(row[colKey]) ? row[colKey] : [];
        colArr.forEach((field) => {
          layoutRow.row.push({
            field: field.column_name,
            colSize: `${getColClass(totalFieldsInRow)} mb-3`,
          });
        });
      });

      generatedLayout.push(layoutRow);
    });

    // === Optional: Inject into layout ===
    let injectedLayout = layout;
    if (autoInjectLayout && layout.length > 0 && generatedLayout.length > 0) {
      const existingFields = new Set(
        layout.flatMap((row) => row.row?.map?.((r) => r.field)).filter(Boolean)
      );
      const customRows = generatedLayout.filter(
        (row) => row.row?.some((r) => !existingFields.has(r.field))
      );

      const merged = [];
      let injected = false;

      for (const row of layout) {
        if (Array.isArray(row.row)) {
          const newRow = { ...row, row: [] };

          for (const col of row.row) {
            if (col?.customFieldsPlaceholder && !injected) {
              merged.push(...customRows);
              injected = true;
            } else {
              newRow.row.push(col);
            }
          }

          if (newRow.row.length > 0) merged.push(newRow);
        } else if (row.type === 'customSplit') {
          const newLeft = [];
          const newRight = [];

          for (const col of row.left || []) {
            if (col?.customFieldsPlaceholder && !injected) {
              customRows.forEach((customRow) => newLeft.push(...customRow.row));
              injected = true;
            } else newLeft.push(col);
          }

          for (const col of row.right || []) {
            if (col?.customFieldsPlaceholder && !injected) {
              customRows.forEach((customRow) => newRight.push(...customRow.row));
              injected = true;
            } else newRight.push(col);
          }

          merged.push({ ...row, left: newLeft, right: newRight });
        }
      }

      if (!injected) merged.push(...customRows);
      injectedLayout = merged;
    }

    return {
      customFields,
      extendedDefaults,
      extendedValidations,
      extendedLabels,
      extendedOverrides,
      extendedSchema,
      generatedLayout,
      injectedLayout, // use this in setMergedLayout()
    };
  } catch (error) {
    console.error('Error fetching custom fields:', error);
    throw error;
  }
}
